import { useReducer } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { AlertTriangle, CalendarClock } from 'lucide-react'
import apiClient, { getApiErrorMessage } from '@/lib/api-client'
import { initiateBillingCheckout } from '@/lib/billing-checkout'
import { formatAmount } from '@/lib/billing-format'
import { useOrgStore } from '@/lib/store'
import { MpAlertBanner, MpButton } from '@/ui/components'

// Server-computed (T-249 / T-248) — the frontend only displays these; it never computes cost.
interface RenewalSummary {
  has_upcoming_renewal: boolean
  show_banner: boolean
  subscription_expired: boolean
  valid_until: string | null
  days_until_renewal: number | null
  currency: string
  monthly_cost_minor: number
  available_minor: number
  shortfall_minor: number
  positions: number
  products: number
  plan_id: string
  plan_name: string
  plan_price_minor: number
  plan_is_self_service: boolean
}

const DAY_MS = 86_400_000

// Snooze the banner in localStorage. Keyed to (org, kind, valid_until) so it re-appears on a new
// renewal cycle. Pre-renewal snoozes until the renewal date; expired snoozes 3 days (access is lost —
// re-surface so the user doesn't forget).
function snoozeStorageKey(orgId: string | null | undefined, kind: string, validUntil: string | null) {
  return `mph.renewal-banner.${orgId ?? 'none'}.${kind}.${validUntil ?? 'na'}`
}
function isSnoozed(key: string) {
  try {
    const raw = localStorage.getItem(key)
    return raw ? Date.now() < Number(raw) : false
  } catch {
    return false
  }
}
function setSnooze(key: string, untilMs: number) {
  try {
    localStorage.setItem(key, String(untilMs))
  } catch {
    /* localStorage unavailable — banner simply stays visible */
  }
}

/**
 * Global subscription banner. Two states, server-driven (GET /api/v1/billing/renewal-summary/):
 * - `subscription_expired`: the plan lapsed → prompt to restore access (danger).
 * - `show_banner`: renewal within 7 days → remind to pay the plan; add a monitoring warning only when
 *   the monthly monitoring cost exceeds the balance available at renewal (rare).
 * The primary CTA starts checkout for the plan directly (one-click), so the user doesn't have to hunt
 * for a renew action on the billing page. Dismissible with snooze. Balance lives in the header.
 */
export function RenewalBanner() {
  const activeOrgId = useOrgStore((state) => state.activeOrgId)
  const { orgId } = useParams()
  const billingPath = orgId ? `/o/${orgId}/billing` : '/billing'
  const [, forceRender] = useReducer((x) => x + 1, 0)

  const { data } = useQuery<RenewalSummary>({
    queryKey: ['billing', 'renewal-summary', activeOrgId],
    queryFn: async () => (await apiClient.get('/api/v1/billing/renewal-summary/')).data,
    enabled: Boolean(activeOrgId),
    staleTime: 60_000,
  })

  const checkout = useMutation({
    mutationFn: async (planId: string) => {
      if (!activeOrgId) throw new Error('Active organization is required for checkout.')
      return initiateBillingCheckout(activeOrgId, { planId })
    },
    onSuccess: (result) => {
      if (result.status === 'completed') toast.success('Оплату вже зараховано.')
      else if (result?.url) window.location.href = result.url
      else if (result.pending) toast.info('Запит на оплату ще обробляється. Новий рахунок не створено.')
      else if (result.terminal) toast.info('Попередній рахунок завершився. Натисніть «Оплатити» ще раз.')
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Не вдалося створити оплату. Спробуйте на сторінці підписки.')),
  })

  if (!data || (!data.show_banner && !data.subscription_expired)) return null

  const isExpired = data.subscription_expired
  const snoozeKey = snoozeStorageKey(activeOrgId, isExpired ? 'expired' : 'renewal', data.valid_until)
  if (isSnoozed(snoozeKey)) return null

  const dismiss = () => {
    const untilMs = isExpired
      ? Date.now() + 3 * DAY_MS
      : data.valid_until
        ? new Date(data.valid_until).getTime()
        : Date.now() + DAY_MS
    setSnooze(snoozeKey, untilMs)
    forceRender()
  }

  const currency = data.currency || 'UAH'
  const planLabel = data.plan_name ? ` ${data.plan_name}` : ''
  const canCheckout = data.plan_is_self_service && data.plan_price_minor > 0 && Boolean(data.plan_id)
  const startCheckout = () => checkout.mutate(data.plan_id)

  if (isExpired) {
    return (
      <MpAlertBanner
        tone="danger"
        icon={AlertTriangle}
        title={`Підписку${planLabel} не поновлено`}
        className="mb-4"
        onClose={dismiss}
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span>
            {canCheckout ? (
              <>
                Оплатіть <strong>{formatAmount(data.plan_price_minor, currency)}</strong>, щоб відновити
                доступ до моніторингу.
              </>
            ) : (
              'Поновіть підписку, щоб відновити доступ до моніторингу.'
            )}
          </span>
          {canCheckout ? (
            <MpButton size="sm" variant="primary" disabled={checkout.isPending} onClick={startCheckout}>
              Поновити підписку
            </MpButton>
          ) : (
            <Link to={billingPath} className="font-medium underline underline-offset-2">
              Обрати тариф
            </Link>
          )}
        </div>
      </MpAlertBanner>
    )
  }

  const hasShortfall = data.shortfall_minor > 0
  const days = data.days_until_renewal ?? 0
  const dayLabel = `${days} ${days === 1 ? 'день' : 'дн.'}`

  return (
    <MpAlertBanner
      tone={hasShortfall ? 'warning' : 'info'}
      icon={hasShortfall ? AlertTriangle : CalendarClock}
      title={`Поновлення підписки${planLabel} через ${dayLabel}`}
      className="mb-4"
      onClose={dismiss}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {canCheckout ? (
          <span>
            Оплатіть <strong>{formatAmount(data.plan_price_minor, currency)}</strong>, щоб продовжити доступ.
          </span>
        ) : null}
        {hasShortfall ? (
          <span>
            Балансу не вистачить на моніторинг — докиньте{' '}
            <strong>{formatAmount(data.shortfall_minor, currency)}</strong>.
          </span>
        ) : null}
        {canCheckout ? (
          <MpButton
            size="sm"
            variant={hasShortfall ? 'secondary' : 'primary'}
            disabled={checkout.isPending}
            onClick={startCheckout}
          >
            Оплатити
          </MpButton>
        ) : (
          <Link to={billingPath} className="font-medium underline underline-offset-2">
            Переглянути підписку
          </Link>
        )}
        {hasShortfall ? (
          <Link to={billingPath} className="font-medium underline underline-offset-2">
            Поповнити баланс
          </Link>
        ) : null}
      </div>
    </MpAlertBanner>
  )
}
